import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Heart, Moon, Flame, TrendingUp, ClipboardList, Activity } from '@/utils/icons';
import AthleteStatusCard from './AthleteStatusCard';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import { getApiUrl } from '@/config/api';
import type { InjuryRecordEntry } from '@/types';
import {
  BodySilhouette,
  FRONT_REGIONS,
  BACK_REGIONS,
  FRONT_POSITIONS,
  BACK_POSITIONS,
} from '@/components/shared/BodyRegionMap';

interface RegionalPainEntry {
  body_region: string;
  pain_level: number;
  pain_type: string;
  is_new_onset: boolean;
  report_date: string;
}

/** Shape of a single daily check-in record from the API. */
interface DailyCheckInEntry {
  check_in_date: string;
  soreness: number;
  motivation: number;
  mental_fatigue: number;
  session_rpe: number;
  notes: string;
  attended: boolean;
  regional_pain?: RegionalPainEntry[];
}

const BODY_REGION_LABELS: Record<string, string> = {
  head_neck: 'Head / Neck',
  left_shoulder: 'L. Shoulder',
  right_shoulder: 'R. Shoulder',
  upper_back: 'Upper Back',
  lower_back: 'Lower Back',
  left_hip: 'L. Hip',
  right_hip: 'R. Hip',
  left_quad: 'L. Quad',
  right_quad: 'R. Quad',
  left_hamstring: 'L. Hamstring',
  right_hamstring: 'R. Hamstring',
  left_knee: 'L. Knee',
  right_knee: 'R. Knee',
  left_ankle: 'L. Ankle',
  right_ankle: 'R. Ankle',
  left_calf: 'L. Calf',
  right_calf: 'R. Calf',
  other: 'Other',
};

function painColor(level: number): string {
  if (level <= 3) return '#22c55e';
  if (level <= 6) return '#eab308';
  return '#ef4444';
}

// ---------------------------------------------------------------------------
// Regional Pain Trend — data helpers
// ---------------------------------------------------------------------------

interface PainDataPoint {
  date: string;
  pain_level: number;
  pain_type: string;
  is_new_onset: boolean;
}

interface RegionTimeSeries {
  region: string;
  label: string;
  latestType: string;
  latestLevel: number;
  trend: 'up' | 'down' | 'stable';
  points: (PainDataPoint | null)[]; // null = no report for that day
  dates: string[];
}

function buildRegionTimeSeries(checkins: DailyCheckInEntry[], windowDays: number): RegionTimeSeries[] {
  const window = checkins.slice(-windowDays);
  if (window.length < 2) return [];

  // Collect all regions that appear in the window
  const regionSet = new Set<string>();
  for (const c of window) {
    for (const rp of c.regional_pain ?? []) {
      regionSet.add(rp.body_region);
    }
  }
  if (regionSet.size === 0) return [];

  const dates = window.map(c => c.check_in_date);

  const series: RegionTimeSeries[] = [];
  for (const region of regionSet) {
    const points: (PainDataPoint | null)[] = window.map(c => {
      const match = (c.regional_pain ?? []).find(rp => rp.body_region === region);
      return match ? { date: c.check_in_date, pain_level: match.pain_level, pain_type: match.pain_type, is_new_onset: match.is_new_onset } : null;
    });

    const dataPoints = points.filter((p): p is PainDataPoint => p !== null);
    if (dataPoints.length === 0) continue;

    const latest = dataPoints[dataPoints.length - 1];

    // Trend: compare avg of first half vs last half of actual data points
    let trend: 'up' | 'down' | 'stable' = 'stable';
    if (dataPoints.length >= 2) {
      const mid = Math.ceil(dataPoints.length / 2);
      const earlyAvg = dataPoints.slice(0, mid).reduce((s, p) => s + p.pain_level, 0) / mid;
      const lateAvg = dataPoints.slice(mid).reduce((s, p) => s + p.pain_level, 0) / (dataPoints.length - mid);
      const diff = lateAvg - earlyAvg;
      if (diff >= 1) trend = 'up';
      else if (diff <= -1) trend = 'down';
    }

    series.push({
      region,
      label: BODY_REGION_LABELS[region] || region,
      latestType: latest.pain_type,
      latestLevel: latest.pain_level,
      trend,
      points,
      dates,
    });
  }

  // Sort: escalating first, then by latest pain level descending
  series.sort((a, b) => {
    const trendOrder = { up: 0, stable: 1, down: 2 };
    if (trendOrder[a.trend] !== trendOrder[b.trend]) return trendOrder[a.trend] - trendOrder[b.trend];
    return b.latestLevel - a.latestLevel;
  });

  return series;
}

// ---------------------------------------------------------------------------
// Expandable note for daily reports
// ---------------------------------------------------------------------------

const ExpandableNote: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <span
      className={`text-[10px] theme-text-primary italic flex-1 leading-snug cursor-pointer ${expanded ? '' : 'line-clamp-2'}`}
      onClick={() => setExpanded(v => !v)}
      title={expanded ? 'Click to collapse' : 'Click to expand'}
    >
      &ldquo;{text}&rdquo;
    </span>
  );
};

// ---------------------------------------------------------------------------
// SVG Sparkline
// ---------------------------------------------------------------------------

const SPARKLINE_W = 200;
const SPARKLINE_H = 24;

const PainSparkline: React.FC<{ points: (PainDataPoint | null)[]; lineColor: string }> = ({ points, lineColor }) => {
  const segments: { idx: number; y: number; yPct: number; color: string; isNewOnset: boolean }[] = [];
  const total = points.length;

  for (let i = 0; i < total; i++) {
    const p = points[i];
    if (!p) continue;
    const y = SPARKLINE_H - ((p.pain_level / 10) * (SPARKLINE_H - 4)) - 2;
    const yPct = (y / SPARKLINE_H) * 100;
    segments.push({ idx: i, y, yPct, color: painColor(p.pain_level), isNewOnset: p.is_new_onset });
  }

  if (segments.length === 0) return null;

  // Dots align with justify-between date labels: 0% to 100%
  const dotXPct = (idx: number) => total > 1 ? (idx / (total - 1)) * 100 : 50;

  // SVG polyline — same positions, butt linecap so line stops at dot centers
  const step = total > 1 ? SPARKLINE_W / (total - 1) : 0;
  const polylinePoints = segments.map(s => `${s.idx * step},${s.y}`).join(' ');

  return (
    <div className="relative w-full" style={{ height: SPARKLINE_H, overflow: 'visible' }}>
      {/* Line layer — stretches horizontally via preserveAspectRatio="none" */}
      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${SPARKLINE_W} ${SPARKLINE_H}`} preserveAspectRatio="none">
        {segments.length > 1 && (
          <polyline points={polylinePoints} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="butt" />
        )}
      </svg>
      {/* Dot layer — positioned with CSS percentages so circles stay round */}
      {segments.map((s, i) => (
        <React.Fragment key={i}>
          {s.isNewOnset && (
            <div
              className="absolute rounded-full border-[1.5px] border-amber-400"
              style={{ left: `${dotXPct(s.idx)}%`, top: `${s.yPct}%`, width: 10, height: 10, transform: 'translate(-50%, -50%)' }}
            />
          )}
          <div
            className="absolute rounded-full"
            style={{
              left: `${dotXPct(s.idx)}%`,
              top: `${s.yPct}%`,
              width: 5,
              height: 5,
              transform: 'translate(-50%, -50%)',
              backgroundColor: s.isNewOnset ? '#f59e0b' : s.color,
            }}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Regional Pain Trend section
// ---------------------------------------------------------------------------

const TREND_ARROWS: Record<string, { symbol: string; color: string }> = {
  up: { symbol: '\u2191', color: '#ef4444' },
  down: { symbol: '\u2193', color: '#22c55e' },
  stable: { symbol: '\u2192', color: '#6b7280' },
};

// ---------------------------------------------------------------------------
// Pain Region Detail Panel
// ---------------------------------------------------------------------------

const TREND_LABELS: Record<string, { label: string; color: string }> = {
  up: { label: 'Escalating', color: '#ef4444' },
  down: { label: 'Improving', color: '#22c55e' },
  stable: { label: 'Stable', color: '#6b7280' },
};

interface PainRegionDetailProps {
  series: RegionTimeSeries[];
  selectedRegion: string;
  checkins: DailyCheckInEntry[];
  onSelectRegion: (region: string) => void;
  injuries?: InjuryRecordEntry[];
}

// ---------------------------------------------------------------------------
// Body Map Panel (left column, desktop only)
// ---------------------------------------------------------------------------

interface BodyMapPanelProps {
  series: RegionTimeSeries[];
  selectedRegion: string;
  onSelectRegion: (region: string) => void;
}

const BodyMapPanel: React.FC<BodyMapPanelProps> = ({ series, selectedRegion, onSelectRegion }) => {
  const activeRegions = series.map(s => ({
    region: s.region,
    pain_level: s.latestLevel,
    isSelected: s.region === selectedRegion,
  }));

  const renderView = (
    regionFilter: Set<string>,
    positions: Record<string, { x: number; y: number; w: number; h: number }>,
    label: string,
  ) => (
    <div className="text-center">
      <svg viewBox="0 0 100 200" className="w-[70px] h-[140px]">
        <BodySilhouette fillColor="#525252" opacity={0.25} />
        {activeRegions
          .filter(r => regionFilter.has(r.region))
          .map(r => {
            const pos = positions[r.region];
            if (!pos) return null;
            const color = painColor(r.pain_level);
            return (
              <g key={r.region} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onSelectRegion(r.region); }}>
                <rect
                  x={pos.x - pos.w / 2} y={pos.y - pos.h / 2}
                  width={pos.w} height={pos.h} rx={3}
                  fill={color} fillOpacity={r.isSelected ? 0.45 : 0.2}
                  stroke={color} strokeWidth={r.isSelected ? 1.5 : 0.8}
                />
                <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="7" fontWeight="bold" fill={color} opacity={r.isSelected ? 1 : 0.6}>
                  {r.pain_level}
                </text>
              </g>
            );
          })}
      </svg>
      <div className="text-[8px] theme-text-secondary mt-0.5">{label}</div>
    </div>
  );

  return (
    <div className="w-[120px] flex-shrink-0 p-2.5 rounded-lg bg-black/[0.03] dark:bg-white/[0.03] border border-gray-200/50 dark:border-gray-700/50 flex flex-col items-center gap-1">
      {renderView(FRONT_REGIONS, FRONT_POSITIONS, 'Front')}
      {renderView(BACK_REGIONS, BACK_POSITIONS, 'Back')}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Daily Reports Panel (below sparklines, desktop only)
// ---------------------------------------------------------------------------

interface DailyReportsPanelProps {
  series: RegionTimeSeries[];
  selectedRegion: string;
  checkins: DailyCheckInEntry[];
  injuries?: InjuryRecordEntry[];
}

const DailyReportsPanel: React.FC<DailyReportsPanelProps> = ({ series, selectedRegion, checkins, injuries = [] }) => {
  const selected = series.find(s => s.region === selectedRegion);
  if (!selected) return null;

  const trendInfo = TREND_LABELS[selected.trend];
  const dataPoints = selected.points.filter((p): p is PainDataPoint => p !== null);
  const reportedDays = dataPoints.length;
  const totalDays = selected.points.length;
  const hasNewOnset = dataPoints.some(p => p.is_new_onset);

  const firstReported = dataPoints[0];
  const firstDate = firstReported ? new Date(firstReported.date + 'T00:00:00') : null;
  const firstDateStr = firstDate
    ? firstDate.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })
    : '';

  const window = checkins.slice(-7);
  const dailyEntries = window
    .map(c => {
      const match = (c.regional_pain ?? []).find(rp => rp.body_region === selectedRegion);
      if (!match) return null;
      return {
        date: c.check_in_date,
        pain_level: match.pain_level,
        pain_type: match.pain_type,
        is_new_onset: match.is_new_onset,
        notes: c.notes || '',
      };
    })
    .filter((e): e is NonNullable<typeof e> => e !== null)
    .reverse();

  return (
    <div className="pt-3 mt-3 border-t border-gray-200/50 dark:border-gray-700/50">
      {/* Inline header: region name + badges + stats */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[9px] theme-text-secondary font-semibold uppercase tracking-wide">Daily Reports</span>
        <span className="text-[11px] font-semibold theme-text-primary">{selected.label}</span>
        <span className="text-[11px] font-bold" style={{ color: painColor(selected.latestLevel) }}>
          {selected.latestLevel}/10
        </span>
        <span
          className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: `${trendInfo.color}20`, color: trendInfo.color }}
        >
          {TREND_ARROWS[selected.trend].symbol} {trendInfo.label}
        </span>
        {hasNewOnset && (
          <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
            New onset
          </span>
        )}
        <span className="flex-1" />
        <span className="text-[10px] theme-text-secondary">
          {reportedDays} of {totalDays} days{firstDateStr && <> &middot; Since <span className="text-amber-500">{firstDateStr}</span></>}
        </span>
      </div>

      {/* Injury history context */}
      {(() => {
        const regionInjury = injuries.find(inj =>
          inj.body_region === selectedRegion && (inj.is_active || inj.days_since <= 90)
        );
        if (!regionInjury) return null;
        return (
          <div className={`mb-2 text-[10px] px-2 py-1.5 rounded ${
            regionInjury.is_active
              ? 'bg-red-500/10 text-red-400 border border-red-500/20'
              : 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
          }`}>
            {regionInjury.is_active
              ? `Active ${regionInjury.severity} injury since ${new Date(regionInjury.injury_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
              : `Previous ${regionInjury.severity} injury \u2014 ${regionInjury.days_since}d ago (resolved)`
            }
          </div>
        );
      })()}

      {/* Daily entries */}
      <div className="space-y-0">
        {dailyEntries.map((entry) => {
          const date = new Date(entry.date + 'T00:00:00');
          const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
          const color = painColor(entry.pain_level);
          return (
            <div
              key={entry.date}
              className="flex items-start gap-2 py-1.5 border-b border-gray-200/30 dark:border-gray-700/30 last:border-b-0"
            >
              <span className="w-[50px] flex-shrink-0 text-[10px] theme-text-secondary">{dateStr}</span>
              <span className="flex items-center gap-1 w-[38px] flex-shrink-0">
                <span
                  className="inline-block w-[5px] h-[5px] rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: entry.is_new_onset ? '#f59e0b' : color,
                    boxShadow: entry.is_new_onset ? '0 0 0 2px var(--color-bg-card, #1a1a2e), 0 0 0 3px #f59e0b' : 'none',
                  }}
                />
                <span className="text-[10px] font-semibold" style={{ color }}>{entry.pain_level}/10</span>
              </span>
              {entry.notes
                ? <ExpandableNote text={entry.notes} />
                : <span className="text-[10px] theme-text-secondary flex-1">--</span>
              }
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mobile Bottom Sheet Detail
// ---------------------------------------------------------------------------

const MobilePainDetail: React.FC<PainRegionDetailProps & { onClose: () => void }> = (props) => {
  const { series, selectedRegion, checkins, onSelectRegion, onClose, injuries = [] } = props;
  const selected = series.find(s => s.region === selectedRegion);

  // Drag-to-dismiss state
  const sheetRef = React.useRef<HTMLDivElement>(null);
  const dragStartY = React.useRef<number | null>(null);
  const [dragOffset, setDragOffset] = React.useState(0);

  const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
    dragStartY.current = e.clientY;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = React.useCallback((e: React.PointerEvent) => {
    if (dragStartY.current === null) return;
    const dy = e.clientY - dragStartY.current;
    setDragOffset(Math.max(0, dy)); // only allow dragging down
  }, []);

  const handlePointerUp = React.useCallback(() => {
    if (dragOffset > 100) {
      onClose();
    }
    dragStartY.current = null;
    setDragOffset(0);
  }, [dragOffset, onClose]);

  if (!selected) return null;

  const trendInfo = TREND_LABELS[selected.trend];
  const dataPoints = selected.points.filter((p): p is PainDataPoint => p !== null);
  const reportedDays = dataPoints.length;
  const totalDays = selected.points.length;
  const hasNewOnset = dataPoints.some(p => p.is_new_onset);
  const firstReported = dataPoints[0];
  const firstDate = firstReported ? new Date(firstReported.date + 'T00:00:00') : null;
  const firstDateStr = firstDate
    ? firstDate.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })
    : '';

  const window = checkins.slice(-7);
  const dailyEntries = window
    .map(c => {
      const match = (c.regional_pain ?? []).find(rp => rp.body_region === selectedRegion);
      if (!match) return null;
      return {
        date: c.check_in_date,
        pain_level: match.pain_level,
        pain_type: match.pain_type,
        is_new_onset: match.is_new_onset,
        notes: c.notes || '',
      };
    })
    .filter((e): e is NonNullable<typeof e> => e !== null)
    .reverse();

  const activeRegions = series.map(s => ({
    region: s.region,
    pain_level: s.latestLevel,
    isSelected: s.region === selectedRegion,
  }));

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        style={{ opacity: Math.max(0, 1 - dragOffset / 200) }}
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        ref={sheetRef}
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.3)] border-t border-gray-200/50 dark:border-gray-700/50 p-4 pt-3 max-h-[85vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--bg-card)',
          transform: `translateY(${dragOffset}px)`,
          transition: dragStartY.current !== null ? 'none' : 'transform 0.3s ease-out',
        }}
      >
        {/* Drag handle — wide touch target with small visual bar */}
        <div
          className="flex items-center justify-center py-2 -mt-3 cursor-grab active:cursor-grabbing touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="w-9 h-1 bg-gray-300 dark:bg-white/25 rounded-full" />
        </div>

        {/* Body maps — large, centered at top */}
        <div className="flex justify-center gap-4 mb-3 px-4 py-2 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-gray-200/30 dark:border-gray-700/30">
          <div className="text-center">
            <svg viewBox="0 0 100 200" className="w-[60px] h-[120px]">
              <BodySilhouette fillColor="#525252" opacity={0.25} />
              {activeRegions.filter(r => FRONT_REGIONS.has(r.region)).map(r => {
                const pos = FRONT_POSITIONS[r.region];
                if (!pos) return null;
                const color = painColor(r.pain_level);
                return (
                  <g key={r.region} style={{ cursor: 'pointer' }} onClick={() => onSelectRegion(r.region)}>
                    <rect x={pos.x - pos.w / 2} y={pos.y - pos.h / 2} width={pos.w} height={pos.h} rx={3}
                      fill={color} fillOpacity={r.isSelected ? 0.45 : 0.2} stroke={color} strokeWidth={r.isSelected ? 1.5 : 0.8} />
                    <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="7" fontWeight="bold" fill={color} opacity={r.isSelected ? 1 : 0.5}>
                      {r.pain_level}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div className="text-[9px] theme-text-secondary mt-1 uppercase tracking-wide">Front</div>
          </div>
          <div className="text-center">
            <svg viewBox="0 0 100 200" className="w-[60px] h-[120px]">
              <BodySilhouette fillColor="#525252" opacity={0.25} />
              {activeRegions.filter(r => BACK_REGIONS.has(r.region)).map(r => {
                const pos = BACK_POSITIONS[r.region];
                if (!pos) return null;
                const color = painColor(r.pain_level);
                return (
                  <g key={r.region} style={{ cursor: 'pointer' }} onClick={() => onSelectRegion(r.region)}>
                    <rect x={pos.x - pos.w / 2} y={pos.y - pos.h / 2} width={pos.w} height={pos.h} rx={3}
                      fill={color} fillOpacity={r.isSelected ? 0.45 : 0.2} stroke={color} strokeWidth={r.isSelected ? 1.5 : 0.8} />
                    <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="8" fontWeight="bold" fill={color} opacity={r.isSelected ? 1 : 0.5}>
                      {r.pain_level}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div className="text-[9px] theme-text-secondary mt-1 uppercase tracking-wide">Back</div>
          </div>
        </div>

        {/* Region info band */}
        <div className="pb-3 mb-2.5 border-b border-gray-200/30 dark:border-gray-700/30">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[15px] font-bold theme-text-primary">{selected.label}</span>
            <span className="text-sm font-bold" style={{ color: painColor(selected.latestLevel) }}>
              {selected.latestLevel}/10
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] theme-text-secondary">{selected.latestType}</span>
            <span
              className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${trendInfo.color}20`, color: trendInfo.color }}
            >
              {TREND_ARROWS[selected.trend].symbol} {trendInfo.label}
            </span>
            {hasNewOnset && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                New onset
              </span>
            )}
          </div>
          <div className="text-[10px] theme-text-secondary mt-1.5">
            {reportedDays} of {totalDays} days{firstDateStr && <> &middot; Since {firstDateStr}</>}
          </div>
          {/* Injury history context for this region */}
          {(() => {
            const regionInjury = injuries.find(inj =>
              inj.body_region === selectedRegion && (inj.is_active || inj.days_since <= 90)
            );
            if (!regionInjury) return null;
            return (
              <div className={`mt-2 text-[10px] px-2 py-1.5 rounded ${
                regionInjury.is_active
                  ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                  : 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
              }`}>
                {regionInjury.is_active
                  ? `Active ${regionInjury.severity} injury since ${new Date(regionInjury.injury_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : `Previous ${regionInjury.severity} injury \u2014 ${regionInjury.days_since}d ago (resolved)`
                }
              </div>
            );
          })()}
        </div>

        {/* Daily breakdown */}
        <div className="text-[9px] theme-text-secondary font-semibold uppercase tracking-wide mb-1">Daily Reports</div>
        {dailyEntries.map((entry) => {
          const date = new Date(entry.date + 'T00:00:00');
          const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
          const color = painColor(entry.pain_level);
          return (
            <div key={entry.date} className="flex items-start gap-2 py-1.5 border-b border-gray-200/30 dark:border-gray-700/30 last:border-b-0">
              <span className="w-[45px] flex-shrink-0 text-[10px] theme-text-secondary">{dateStr}</span>
              <span className="flex items-center gap-1 w-[36px] flex-shrink-0">
                <span className="inline-block w-[5px] h-[5px] rounded-full" style={{
                  backgroundColor: entry.is_new_onset ? '#f59e0b' : color,
                  boxShadow: entry.is_new_onset ? '0 0 0 2px var(--bg-card, #161616), 0 0 0 3px #f59e0b' : 'none',
                }} />
                <span className="text-[10px] font-semibold" style={{ color }}>{entry.pain_level}/10</span>
              </span>
              {entry.notes
                ? <ExpandableNote text={entry.notes} />
                : <span className="text-[10px] theme-text-secondary flex-1">--</span>
              }
            </div>
          );
        })}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Injury Badge with tap/hover tooltip
// ---------------------------------------------------------------------------

const InjuryBadge: React.FC<{ injury: InjuryRecordEntry }> = ({ injury }) => {
  const [open, setOpen] = useState(false);
  const badgeRef = useRef<HTMLSpanElement>(null);

  const label = injury.is_active ? 'Injury' : 'Prior';
  const detail = injury.is_active
    ? `Active ${injury.severity} injury since ${new Date(injury.injury_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : `Resolved ${injury.severity} injury, ${injury.days_since}d ago`;

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: PointerEvent) => {
      if (badgeRef.current && !badgeRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [open]);

  return (
    <span
      ref={badgeRef}
      className="relative group/injbadge"
      onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
    >
      <span
        className={`text-[8px] font-semibold px-1 py-px rounded cursor-help ${
          injury.is_active
            ? 'bg-red-500/20 text-red-400'
            : 'bg-gray-500/20 text-gray-400'
        }`}
      >
        {label}
      </span>
      <span
        className={`absolute left-0 top-full z-[9999] mt-1 w-48 px-2.5 py-1.5 rounded-lg border shadow-lg text-[10px] leading-snug transition-all duration-150 ${
          open
            ? 'opacity-100 visible pointer-events-auto'
            : 'opacity-0 invisible pointer-events-none group-hover/injbadge:opacity-100 group-hover/injbadge:visible'
        } bg-neutral-900 border-neutral-600 text-neutral-200`}
      >
        {detail}
        {injury.notes && <div className="mt-0.5 text-neutral-400">{injury.notes}</div>}
      </span>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Regional Pain Trend section (with interactive detail)
// ---------------------------------------------------------------------------

const RegionalPainTrend: React.FC<{ checkins: DailyCheckInEntry[]; injuries?: InjuryRecordEntry[] }> = ({ checkins, injuries = [] }) => {
  const series = useMemo(() => buildRegionTimeSeries(checkins, 7), [checkins]);
  // Auto-select the top (highest priority) region for desktop inline detail;
  // track whether user explicitly tapped to control mobile bottom sheet
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  // Default-select top row once series is available
  if (series.length > 0 && selectedRegion === null) {
    setSelectedRegion(series[0].region);
  }

  if (series.length === 0) return null;

  const handleRowClick = (region: string) => {
    if (selectedRegion === region) {
      // Tapping the already-selected row: on mobile toggle the sheet, on desktop deselect
      setMobileSheetOpen(prev => !prev);
    } else {
      setSelectedRegion(region);
      setMobileSheetOpen(true);
    }
  };

  // Build a lookup: region -> most relevant injury (active first, then recent resolved)
  const injuryByRegion = useMemo(() => {
    const map: Record<string, InjuryRecordEntry> = {};
    for (const inj of injuries) {
      if (inj.is_active || inj.days_since <= 90) {
        const existing = map[inj.body_region];
        if (!existing || inj.is_active || inj.days_since < existing.days_since) {
          map[inj.body_region] = inj;
        }
      }
    }
    return map;
  }, [injuries]);

  const sparklineRows = (
    <>
      <div className="space-y-2.5">
        {series.map((s) => {
          const arrow = TREND_ARROWS[s.trend];
          const isSelected = selectedRegion === s.region;
          const rowColor = painColor(s.latestLevel);
          const regionInjury = injuryByRegion[s.region];
          return (
            <div
              key={s.region}
              className="flex items-center gap-3 rounded-md px-2 py-1.5 cursor-pointer transition-colors"
              style={{
                border: isSelected ? `1px solid ${rowColor}50` : '1px solid transparent',
                backgroundColor: isSelected ? `${rowColor}10` : 'transparent',
              }}
              onClick={() => handleRowClick(s.region)}
            >
              <div className="w-[85px] flex-shrink-0">
                <div className="flex items-center gap-1">
                  <span className={`text-[11px] leading-tight ${isSelected ? 'font-semibold theme-text-primary' : 'theme-text-primary'}`}>
                    {s.label}
                  </span>
                  {regionInjury && (
                    <InjuryBadge injury={regionInjury} />
                  )}
                </div>
                <div className="text-[9px] theme-text-secondary">{s.latestType}</div>
              </div>
              <div className="w-[20px] flex-shrink-0 flex items-center justify-center">
                <span className="text-[8px] theme-text-secondary" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Pain</span>
              </div>
              <div className="flex-1 h-7 rounded bg-black/5 dark:bg-white/5 px-1 flex items-center">
                <PainSparkline points={s.points} lineColor={painColor(s.latestLevel)} />
              </div>
              <div className="w-[35px] text-right">
                <span className="text-sm font-bold" style={{ color: painColor(s.latestLevel) }}>
                  {s.latestLevel}
                </span>
              </div>
              <div className="w-4 text-center text-sm" style={{ color: arrow.color }}>
                {arrow.symbol}
              </div>
            </div>
          );
        })}
      </div>
      {/* X-axis date labels */}
      {series.length > 0 && (
        <div className="flex items-start gap-3 mt-1">
          <div className="w-[85px] flex-shrink-0" />
          <div className="w-[20px] flex-shrink-0" />
          <div className="flex-1 px-1 flex justify-between">
            {series[0].dates.map((d) => {
              const date = new Date(d + 'T00:00:00');
              const day = date.toLocaleDateString('en-US', { weekday: 'short' });
              const md = `${date.getMonth() + 1}/${date.getDate()}`;
              return (
                <span key={d} className="text-[8px] theme-text-secondary text-center leading-tight">
                  {day}<br />{md}
                </span>
              );
            })}
          </div>
          <div className="w-[35px] flex-shrink-0" />
          <div className="w-4 flex-shrink-0" />
        </div>
      )}
      {/* Legend */}
      <div className="flex gap-4 mt-3 pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
        <div className="flex items-center gap-1">
          <svg width="12" height="12"><circle cx="6" cy="6" r="4" fill="none" stroke="#f59e0b" strokeWidth="1.5"/><circle cx="6" cy="6" r="2" fill="#f59e0b"/></svg>
          <span className="text-[9px] theme-text-secondary">New onset</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px]" style={{ color: '#ef4444' }}>{'\u2191'}</span>
          <span className="text-[9px] theme-text-secondary">Escalating</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px]" style={{ color: '#22c55e' }}>{'\u2193'}</span>
          <span className="text-[9px] theme-text-secondary">Improving</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px]" style={{ color: '#6b7280' }}>{'\u2192'}</span>
          <span className="text-[9px] theme-text-secondary">Stable</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
      <p className="text-xs theme-text-secondary mb-3 font-medium">
        Pain History (7 days)
      </p>

      {/* Desktop: body map left, sparklines + daily reports right */}
      <div className="flex gap-4">
        {/* Body map panel (desktop only) */}
        {selectedRegion && (
          <div className="hidden sm:block transition-all duration-300 ease-in-out">
            <BodyMapPanel
              series={series}
              selectedRegion={selectedRegion}
              onSelectRegion={handleRowClick}
            />
          </div>
        )}

        {/* Sparkline column + daily reports below */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1">
            {sparklineRows}
          </div>
          {/* Daily reports below sparklines (desktop only) */}
          {selectedRegion && (
            <div className="hidden sm:block">
              <DailyReportsPanel
                series={series}
                selectedRegion={selectedRegion}
                checkins={checkins}
                injuries={injuries}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile bottom sheet — only opens on explicit tap, not auto-select */}
      {selectedRegion && mobileSheetOpen && (
        <div className="sm:hidden">
          <MobilePainDetail
            series={series}
            selectedRegion={selectedRegion}
            checkins={checkins}
            injuries={injuries}
            onSelectRegion={handleRowClick}
            onClose={() => setMobileSheetOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

interface OverviewTabProps {
  athleteData: any;
  isDark: boolean;
  getValidatedStats: () => any;
  connectedSources?: string[];
}

const OverviewTab: React.FC<OverviewTabProps> = ({ athleteData, isDark, getValidatedStats, connectedSources }) => {
  const validatedStats = getValidatedStats();

  // Derive the most recent daily check-in (if any)
  const checkins: DailyCheckInEntry[] = athleteData?.daily_checkins ?? [];
  const latestCheckIn = checkins.length > 0 ? checkins[checkins.length - 1] : null;

  // Fetch injury history for cross-referencing with pain data
  const athleteId = athleteData?.athlete_info?.id;
  const [injuries, setInjuries] = useState<InjuryRecordEntry[]>([]);
  const fetchInjuries = useCallback(async () => {
    if (!athleteId) return;
    const result = await authenticatedFetchWithRetry<{ injuries: InjuryRecordEntry[] }>(
      getApiUrl(`/api/injury-history/${athleteId}/`),
      { method: 'GET' },
      'fetching injury history for pain cross-reference',
    );
    if (result.success && result.data && (result.data as any).status !== 'error') {
      setInjuries(result.data.injuries);
    }
  }, [athleteId]);
  useEffect(() => { fetchInjuries(); }, [fetchInjuries]);

  // Fetch device vitals for Terra-specific tiles (stress, RHR, VO2max).
  // Uses include_details=false to skip expensive readiness/risk computation.
  // TODO: Lift device_vitals from AthleteStatusCard to avoid duplicate endpoint call.
  const [deviceVitals, setDeviceVitals] = useState<any>(null);
  useEffect(() => {
    if (!athleteId) return;
    const run = async () => {
      const result = await authenticatedFetchWithRetry<any>(
        getApiUrl(`/api/athlete-status/${athleteId}/?include_details=false`),
        { method: 'GET' },
        'fetching device vitals',
        1,
      );
      if (result.success && result.data?.device_vitals) {
        setDeviceVitals(result.data.device_vitals);
      }
    };
    run();
  }, [athleteId]);

  return (
    <div className="space-y-6">
      {/* Unified Athlete Status: Readiness + Injury Risk */}
      <AthleteStatusCard
        athleteId={athleteData?.athlete_info?.id}
        isDark={isDark}
        defaultExpanded={true}
        connectedSources={connectedSources}
      />

      {/* Stats Grid — device-aware tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {(() => {
          const hasWhoop = connectedSources?.includes('whoop');
          const hasTerra = connectedSources?.some(s => ['garmin', 'coros', 'oura', 'suunto'].includes(s));
          const v = deviceVitals;

          return [
            // Tile 1: Recovery (WHOOP) or Stress (Terra) or Readiness fallback
            hasWhoop ? {
              title: 'Recovery Score',
              value: validatedStats?.recovery.latest_recovery ? `${validatedStats.recovery.latest_recovery.toFixed(0)}%` : '—',
              subtitle: `Avg: ${validatedStats?.recovery.average_recovery ? validatedStats.recovery.average_recovery.toFixed(0) + '%' : '—'}`,
              Icon: Heart
            } : hasTerra && v?.stress_avg ? {
              title: 'Stress Level',
              value: `${Math.round(v.stress_avg)}`,
              subtitle: v.source ? `via ${v.source}` : 'Terra device',
              Icon: Activity
            } : {
              title: 'Recovery',
              value: '—',
              subtitle: 'No recovery data',
              Icon: Heart
            },

            // Tile 2: Sleep Quality (works for all devices)
            {
              title: 'Sleep Quality',
              value: validatedStats?.sleep.latest_quality ? `${validatedStats.sleep.latest_quality.toFixed(0)}%` : '—',
              subtitle: `${validatedStats?.sleep.latest_duration ? validatedStats.sleep.latest_duration.toFixed(1) + 'h' : '—'} duration`,
              Icon: Moon
            },

            // Tile 3: Strain (WHOOP) or Activity Duration (Terra)
            hasWhoop ? {
              title: 'Daily Strain',
              value: validatedStats?.activity.latest_strain ? `${validatedStats.activity.latest_strain.toFixed(1)}` : '—',
              subtitle: `Avg: ${validatedStats?.activity.average_strain ? validatedStats.activity.average_strain.toFixed(1) : '—'}`,
              Icon: Flame
            } : {
              title: 'Activity',
              value: validatedStats?.activity.data_points ? `${validatedStats.activity.data_points} sessions` : '—',
              subtitle: validatedStats?.activity.average_strain ? `Avg strain: ${validatedStats.activity.average_strain.toFixed(1)}` : '7-day summary',
              Icon: Flame
            },

            // Tile 4: RHR (Terra) or Data Points (WHOOP/fallback)
            hasTerra && v?.resting_hr ? {
              title: 'Resting HR',
              value: `${Math.round(v.resting_hr)} bpm`,
              subtitle: v.vo2_max ? `VO₂ max: ${v.vo2_max.toFixed(1)}` : (v.spo2_avg ? `SpO₂: ${v.spo2_avg.toFixed(0)}%` : v.source || ''),
              Icon: Heart
            } : {
              title: 'Data Points',
              value: `${Math.max(validatedStats?.recovery.data_points || 0, validatedStats?.sleep.data_points || 0, validatedStats?.activity.data_points || 0)}`,
              subtitle: `${athleteData.data_summary.analysis_period_days} day period`,
              Icon: TrendingUp
            },
          ];
        })().map((stat, index) => {
          const iconColors = ['cgm-icon-recovery', 'cgm-icon-sleep', 'cgm-icon-strain', 'cgm-icon-data'];
          return (
            <div
              key={stat.title}
              className={`cgm-metric-card`}
            >
              <div className="flex items-center gap-3 mb-4">
                <stat.Icon className={`w-8 h-8 flex-shrink-0 ${iconColors[index]}`} />
                <div className="text-sm cgm-text-secondary font-medium">{stat.title}</div>
              </div>
              <div className="text-2xl font-bold theme-text-primary mb-1">{stat.value}</div>
              <div className="text-sm theme-text-secondary">{stat.subtitle}</div>
            </div>
          );
        })}
      </div>

      {/* Daily Check-In Feedback Card */}
      {latestCheckIn && (
        <div className="cgm-card rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardList className="w-6 h-6 cgm-icon-data" />
            <h3 className="text-lg font-semibold theme-text-primary">
              Athlete Feedback
            </h3>
            <span className="ml-auto text-xs theme-text-secondary">
              {new Date(latestCheckIn.check_in_date + 'T12:00:00').toLocaleDateString(
                'en-US',
                { month: 'short', day: 'numeric' }
              )}
            </span>
          </div>

          {!latestCheckIn.attended ? (
            <p className="text-sm theme-text-secondary italic">
              Athlete reported they did not train on this day.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Soreness', value: latestCheckIn.soreness },
                { label: 'Motivation', value: latestCheckIn.motivation },
                { label: 'Mental Fatigue', value: latestCheckIn.mental_fatigue },
                { label: 'RPE', value: latestCheckIn.session_rpe },
              ].map((metric) => (
                <div key={metric.label} className="text-center">
                  <div className="text-2xl font-bold theme-text-primary">
                    {metric.value}
                    <span className="text-xs font-normal theme-text-secondary">
                      /10
                    </span>
                  </div>
                  <div className="text-xs theme-text-secondary mt-1">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Regional Pain Reports */}
          {latestCheckIn.regional_pain && latestCheckIn.regional_pain.length > 0 && (
            <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
              <p className="text-xs theme-text-secondary mb-2 font-medium">
                Reported Pain Regions
              </p>
              <div className="flex flex-wrap gap-2">
                {latestCheckIn.regional_pain.map((rp, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border"
                    style={{
                      borderColor: `${painColor(rp.pain_level)}40`,
                      backgroundColor: `${painColor(rp.pain_level)}10`,
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: painColor(rp.pain_level) }}
                    />
                    <span className="font-medium theme-text-primary">
                      {BODY_REGION_LABELS[rp.body_region] || rp.body_region}
                    </span>
                    <span style={{ color: painColor(rp.pain_level) }}>
                      {rp.pain_level}/10
                    </span>
                    <span className="theme-text-secondary">
                      ({rp.pain_type})
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Regional Pain Trend Sparklines */}
          <RegionalPainTrend checkins={checkins} injuries={injuries} />

          {latestCheckIn.notes && (
            <p className="mt-4 text-sm theme-text-secondary italic border-t border-gray-200 dark:border-gray-700 pt-3">
              "{latestCheckIn.notes}"
            </p>
          )}

          {/* Mini trend: show last few check-ins as dots */}
          {checkins.length > 1 && (
            <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
              <p className="text-xs theme-text-secondary mb-2">
                RPE trend ({checkins.length} days)
              </p>
              <div className="flex items-end gap-1 h-8">
                {checkins.slice(-14).map((c, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-sm bg-[var(--accent-ui)]"
                    style={{
                      height: `${(c.session_rpe / 10) * 100}%`,
                      opacity: c.attended ? 1 : 0.25,
                    }}
                  />
                ))}
              </div>
              <div className="flex gap-1 mt-1">
                {checkins.slice(-14).map((c, i) => (
                  <div
                    key={i}
                    className="flex-1 text-center text-[9px] theme-text-secondary"
                    style={{ opacity: c.attended ? 1 : 0.4 }}
                  >
                    {c.session_rpe}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
